import { useEffect, useState } from "react";
import api, { formatErr } from "../lib/api";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  ShieldCheck,
  ShieldAlert,
  Clock,
  CheckCircle2,
  XCircle,
  RefreshCw,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import MasterPolicyApplicationForm from "./MasterPolicyApplicationForm";

/**
 * MasterPolicyStatusCard
 *
 * Top-of-page card on /seller/insurance that surfaces the seller's
 * Stage-1 master-policy application state machine:
 *
 *   not_started      → "Apply for Allianz Trade coverage" CTA
 *   pending_review   → "Under review" (3-10 business days)
 *   reapply_pending  → "Reapply under review" + ticket #
 *   approved         → policy summary (policy_id, BU, dates, premium rate)
 *   declined         → reason + "Reapply" button → dialog → ticket auto-opens
 *
 * Calls /api/seller/master-policy/status on mount + after every action.
 */
export const MasterPolicyStatusCard = () => {
  const [state, setState] = useState({
    loading: true,
    status: "not_started",
    application: null,
    tenant_allianz: null,
  });

  const [applyOpen, setApplyOpen] = useState(false);
  const [reapplyOpen, setReapplyOpen] = useState(false);
  const [reapplyReason, setReapplyReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/seller/master-policy/status");
      setState({
        loading: false,
        status: data?.status || "not_started",
        application: data?.application || null,
        tenant_allianz: data?.tenant_allianz || null,
      });
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/seller/master-policy/status");
        if (cancelled) return;
        setState({
          loading: false,
          status: data?.status || "not_started",
          application: data?.application || null,
          tenant_allianz: data?.tenant_allianz || null,
        });
      } catch {
        if (!cancelled) setState((s) => ({ ...s, loading: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleApply = async (payload) => {
    setSubmitting(true);
    setErr("");
    try {
      await api.post("/seller/master-policy/apply", payload);
      toast.success("Application submitted to Allianz Trade underwriting.");
      setApplyOpen(false);
      await load();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReapply = async () => {
    if (reapplyReason.trim().length < 8) return;
    setSubmitting(true);
    setErr("");
    try {
      const { data } = await api.post("/seller/master-policy/reapply", {
        reason: reapplyReason.trim(),
      });
      toast.success(
        `Reapply submitted. Ticket ${data.ticket_number} opened — we'll handle it manually.`,
      );
      setReapplyOpen(false);
      setReapplyReason("");
      await load();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return (
      <div
        className="rounded-2xl border border-foreground/10 bg-white/60 px-5 py-4 text-[12.5px] text-foreground/50"
        data-testid="mp-card-loading"
      >
        Loading master-policy status…
      </div>
    );
  }

  // ─── States ──────────────────────────────────────────────────────────
  const s = state.status;

  // NOT STARTED — primary CTA
  if (s === "not_started") {
    return (
      <>
        <div
          className="rounded-2xl border border-foreground/10 bg-gradient-to-br from-emerald-50/60 to-white px-5 py-4"
          data-testid="mp-card-not-started"
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0 bg-emerald-500 text-white">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] mono uppercase tracking-[0.18em] text-emerald-700">
                Allianz Trade · Master policy
              </div>
              <div className="text-sm font-medium mt-0.5">
                Apply for Allianz Trade trade-credit insurance
              </div>
              <div className="text-[11.5px] text-foreground/65 mt-1 leading-snug">
                Insure up to 90% of any unpaid invoice. Per-buyer covers auto-issue
                once your master policy is approved (3–10 business days).
              </div>
            </div>
            <Button
              onClick={() => setApplyOpen(true)}
              data-testid="mp-apply-btn"
              className="rounded-full bg-foreground text-background hover:bg-foreground/90 shrink-0"
            >
              Apply for coverage <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        </div>

        <ApplyDialog
          open={applyOpen}
          onOpenChange={setApplyOpen}
          onSubmit={handleApply}
          submitting={submitting}
          error={err}
        />
      </>
    );
  }

  // PENDING REVIEW (initial or reapply)
  if (s === "pending_review" || s === "reapply_pending") {
    const isReapply = s === "reapply_pending";
    return (
      <div
        className="rounded-2xl border border-amber-300 bg-amber-50/40 px-5 py-4"
        data-testid={`mp-card-${s}`}
      >
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0 bg-amber-500 text-white">
            <Clock className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] mono uppercase tracking-[0.18em] text-amber-700">
              {isReapply ? "Reapply submitted" : "Under review"}
            </div>
            <div className="text-sm font-medium mt-0.5">
              Allianz Trade is reviewing your master policy
            </div>
            <div className="text-[11.5px] text-foreground/65 mt-1 leading-snug">
              Underwriting typically takes 3–10 business days. We&apos;ll email you
              the moment a decision is made.
              {isReapply && state.application?.reapply_ticket_number ? (
                <>
                  {" "}
                  Reference ticket:{" "}
                  <span className="mono font-medium">
                    {state.application.reapply_ticket_number}
                  </span>
                  .
                </>
              ) : null}
            </div>
            <div className="text-[11px] text-foreground/55 mt-2 flex gap-3 flex-wrap">
              <span>
                Submitted:{" "}
                <span className="mono">
                  {state.application?.submitted_at
                    ? new Date(state.application.submitted_at).toLocaleDateString()
                    : "—"}
                </span>
              </span>
              <span>
                Buyers on roster:{" "}
                <span className="mono">
                  {state.application?.buyer_count_at_submission || 0}
                </span>
              </span>
              <span>
                Requested:{" "}
                <span className="mono">
                  ${(state.application?.requested_aggregate_limit || 0).toLocaleString()}
                </span>
              </span>
            </div>
          </div>
          <Button
            onClick={load}
            variant="outline"
            size="sm"
            data-testid="mp-refresh-btn"
            className="rounded-full shrink-0"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>
    );
  }

  // APPROVED — policy summary
  if (s === "approved") {
    const a = state.application || {};
    const t = state.tenant_allianz || {};
    return (
      <div
        className="rounded-2xl border border-emerald-300 bg-emerald-50/40 px-5 py-4"
        data-testid="mp-card-approved"
      >
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0 bg-emerald-500 text-white">
            <CheckCircle2 className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] mono uppercase tracking-[0.18em] text-emerald-700">
              Master policy · Active
            </div>
            <div className="text-sm font-medium mt-0.5">
              Policy{" "}
              <span className="mono">
                {a.allianz_policy_id || t.policy_id || "—"}
              </span>{" "}
              · Business unit {a.business_unit_code || t.business_unit_code || "US"}
            </div>
            <div className="text-[11.5px] text-foreground/65 mt-1 leading-snug">
              Per-buyer covers now auto-issue as new buyers sign up. Premium
              rate <span className="mono">
                {((a.premium_rate ?? t.premium_rate) * 100 || 0).toFixed(2)}%
              </span>{" "}
              of approved limit · pass-through.
            </div>
            <div className="text-[11px] text-foreground/55 mt-2 flex gap-3 flex-wrap">
              <span>
                Effective:{" "}
                <span className="mono">
                  {a.effective_from || t.effective_from || "—"} →{" "}
                  {a.effective_to || t.effective_to || "—"}
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // DECLINED — show reason + reapply CTA
  if (s === "declined") {
    const a = state.application || {};
    return (
      <>
        <div
          className="rounded-2xl border border-rose-300 bg-rose-50/40 px-5 py-4"
          data-testid="mp-card-declined"
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0 bg-rose-500 text-white">
              <XCircle className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] mono uppercase tracking-[0.18em] text-rose-700">
                Master policy · Declined
              </div>
              <div className="text-sm font-medium mt-0.5">
                Allianz declined your master policy
              </div>
              <div className="text-[12px] text-foreground/75 mt-2 leading-snug bg-white/60 rounded-lg px-3 py-2 border border-rose-200">
                <strong className="text-rose-900">Reason:</strong>{" "}
                {a.decline_reason || "(no reason provided)"}
              </div>
              <div className="text-[11.5px] text-foreground/65 mt-2 leading-snug">
                You can reapply by telling us why we should reconsider. We&apos;ll
                open a manual review ticket and get back to you.
              </div>
            </div>
            <Button
              onClick={() => setReapplyOpen(true)}
              data-testid="mp-reapply-btn"
              className="rounded-full bg-foreground text-background hover:bg-foreground/90 shrink-0"
            >
              Reapply
            </Button>
          </div>
        </div>

        {/* Reapply dialog */}
        <Dialog open={reapplyOpen} onOpenChange={setReapplyOpen}>
          <DialogContent className="max-w-md" data-testid="mp-reapply-dialog">
            <DialogHeader>
              <DialogTitle>Reapply for Allianz Trade coverage</DialogTitle>
              <DialogDescription>
                Tell us why we should reconsider. We&apos;ll open a manual review
                ticket and handle it personally.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Label className="text-[12px]">
                Why should we reconsider?
              </Label>
              <Textarea
                value={reapplyReason}
                onChange={(e) => setReapplyReason(e.target.value)}
                placeholder="e.g. We've added 3 new buyers across different sectors. Concentration on our top buyer dropped from 60% to 28%. Updated financials attached."
                rows={6}
                data-testid="mp-reapply-reason"
                className="text-[13px]"
                maxLength={1000}
              />
              <div className="text-[11px] text-foreground/55 text-right">
                {reapplyReason.length}/1000
              </div>
              {err && (
                <div className="text-[12px] text-rose-700">{err}</div>
              )}
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                variant="ghost"
                onClick={() => setReapplyOpen(false)}
                className="rounded-full"
                data-testid="mp-reapply-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleReapply}
                disabled={reapplyReason.trim().length < 8 || submitting}
                data-testid="mp-reapply-submit"
                className="flex-1 rounded-full bg-foreground text-background hover:bg-foreground/90"
              >
                {submitting ? "Submitting…" : "Submit reapply"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Fallback (shouldn't happen)
  return null;
};

// ─── Apply Dialog (reuses the multi-buyer form) ───────────────────────────
const ApplyDialog = ({ open, onOpenChange, onSubmit, submitting, error }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      className="max-w-2xl max-h-[90vh] overflow-y-auto"
      data-testid="mp-apply-dialog"
    >
      <DialogHeader>
        <DialogTitle>Apply for Allianz Trade master policy</DialogTitle>
        <DialogDescription>
          List your 5+ largest buyers with EIN, legal address, and annual
          sales volume. Underwriting takes 3–10 business days.
        </DialogDescription>
      </DialogHeader>
      <MasterPolicyApplicationForm
        onSubmit={onSubmit}
        onCancel={() => onOpenChange(false)}
        submitting={submitting}
        error={error}
        compact
      />
    </DialogContent>
  </Dialog>
);

export default MasterPolicyStatusCard;
